/**
 * Cache Storage handoff for the exact notification payload a click needs.
 *
 * Both the service worker (on `notificationclick`) and the page (on load)
 * can reach Cache Storage, unlike `localStorage`/`sessionStorage`, which the
 * SW can't use, and unlike IndexedDB, which would need real schema/version
 * handling for something this small. An entry lives only from the moment a
 * notification is clicked to the moment the page reads it — this is not a
 * notification history. Every write and every read prunes: entries older
 * than `MAX_AGE_MS` are dropped, and at most `MAX_ENTRIES` are kept.
 */

export const CACHE_NAME = 'notification-detail-v1';
export const MAX_AGE_MS = 60 * 60 * 1000;
export const MAX_ENTRIES = 10;
const KEY_PREFIX = '/__notification-detail/';

export interface StoredDetail {
  t: string;
  b: string;
  i: string;
}

interface StoredEntry extends StoredDetail {
  /** Stored-at epoch ms, used for expiry. */
  s: number;
}

function keyFor(id: string): string {
  return KEY_PREFIX + encodeURIComponent(id);
}

function isStoredEntry(value: unknown): value is StoredEntry {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return typeof v.t === 'string' && typeof v.b === 'string' && typeof v.i === 'string' && typeof v.s === 'number';
}

/** Drops expired entries and, if still over the cap, the oldest ones. */
export async function pruneDetails(cachesApi: CacheStorage, now: number): Promise<void> {
  try {
    const cache = await cachesApi.open(CACHE_NAME);
    const requests = await cache.keys();
    const entries: { request: Request; storedAt: number }[] = [];
    for (const request of requests) {
      if (!request.url.includes(KEY_PREFIX)) continue;
      const res = await cache.match(request);
      const data = res ? ((await res.json().catch(() => null)) as unknown) : null;
      if (!isStoredEntry(data) || now - data.s > MAX_AGE_MS) {
        await cache.delete(request);
        continue;
      }
      entries.push({ request, storedAt: data.s });
    }
    if (entries.length > MAX_ENTRIES) {
      entries.sort((a, b) => a.storedAt - b.storedAt);
      const excess = entries.slice(0, entries.length - MAX_ENTRIES);
      for (const { request } of excess) await cache.delete(request);
    }
  } catch {
    /* Cache Storage unavailable or throwing — nothing to prune, nothing fatal. */
  }
}

export async function saveDetail(cachesApi: CacheStorage, id: string, detail: StoredDetail, now: number): Promise<void> {
  try {
    const cache = await cachesApi.open(CACHE_NAME);
    const entry: StoredEntry = { ...detail, s: now };
    await cache.put(keyFor(id), new Response(JSON.stringify(entry), { headers: { 'content-type': 'application/json' } }));
  } catch {
    /* Best-effort: if this fails, the page falls back to the normal landing state. */
  }
  await pruneDetails(cachesApi, now);
}

/** Reads and removes the entry for `id` (a detail is meant to be consumed once). */
export async function takeDetail(cachesApi: CacheStorage, id: string, now: number): Promise<StoredDetail | null> {
  let result: StoredDetail | null = null;
  try {
    const cache = await cachesApi.open(CACHE_NAME);
    const request = keyFor(id);
    const res = await cache.match(request);
    if (res) {
      await cache.delete(request);
      const data = (await res.json().catch(() => null)) as unknown;
      if (isStoredEntry(data) && now - data.s <= MAX_AGE_MS) {
        result = { t: data.t, b: data.b, i: data.i };
      }
    }
  } catch {
    result = null;
  }
  await pruneDetails(cachesApi, now);
  return result;
}
